import SlickGrid from "@CubeComponents/html/SlickGrid";
import _ from "lodash";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import IconComponent from "@CubeComponents/icons";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextBoxComponent";
import { Box, Breadcrumbs, Link, Menu, MenuItem } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useTranslation } from "react-i18next";
import { createRoot } from "react-dom/client";
import { fileViewModes } from "./constants";
import { getIconbasedOnResourceType } from "@CubeComponents/constants/globalFunctions";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";

interface FilemanagerFileProps {
  setSelectedFolder: any;
  onFileClick: Function;
  onFileSelect: Function;
  fileManagerData: any;
  contextMenuSettings: any;
  menuOpen: any;
  fileColumns: any;
  tempFolder: any;
  handleSearchFiles: Function;
  onFileLoad: Function;
  beforeSending: Function;
  allowDragAndDropUpload: boolean;
  selectedFolder: any;
  filesViewMode: string;
  setSelectedFilesInIconsMode: any;
  selectedFilesInIconsMode: any;
  setIsViewRendered: any;
  isViewRendered: boolean;
}
const Files = forwardRef(
  (
    {
      setSelectedFolder,
      onFileClick = () => {},
      onFileSelect = () => {},
      fileManagerData = {},
      contextMenuSettings,
      menuOpen,
      fileColumns,
      tempFolder,
      handleSearchFiles = () => {},
      onFileLoad = () => {},
      beforeSending,
      allowDragAndDropUpload,
      selectedFolder,
      filesViewMode = "DETAILED_FILES",
      setSelectedFilesInIconsMode,
      selectedFilesInIconsMode,
      setIsViewRendered,
      isViewRendered,
    }: FilemanagerFileProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const [disabledRows, setDisabledRows] = useState<any>([]);
    const [breadCrumbData, setBreadCrumbData] = useState<any>([]);
    const [filesData, setFilesData] = useState<any>([]);
    const [selectedResources, setSelectedResources] = useState<any>([]);
    const [showPathColumn, setShowPathColumn] = useState(false);
    const [columnDefinitions, setColumnDefinitions] = useState(fileColumns);
    const tempRef = useRef<any>({});
    const dataGrid: any = ref || tempRef;
    const [menuState, setMenuState] = useState<any>({
      open: false,
      source: "",
      position: null,
      data: null,
      options: null,
    });
    const contextMenuSettingsRef = useRef<any>(null);

    useEffect(() => {
      contextMenuSettingsRef.current = contextMenuSettings;
    }, [contextMenuSettings]);

    const setSelectedRows = (indexes: any) => {
      if (Array.isArray(indexes)) {
        if (filesViewMode === fileViewModes.DETAILED_VIEW) {
          dataGrid.current.slickGrid.setSelectedRows(indexes);
        } else if (filesViewMode === fileViewModes.LARGE_ICONS_VIEW) {
          if (indexes?.length == 0) {
            setSelectedFilesInIconsMode([]);
          } else {
            const filesToSelect: any = [];
            indexes.forEach((index) => {
              if (filesData[index]) {
                filesToSelect.push(filesData[index]);
              }
            });
            setSelectedFilesInIconsMode(filesToSelect);
          }
        }
      }
    };

    useEffect(() => {
      if (dataGrid.current) {
        dataGrid.current.setSelectedRows = setSelectedRows;
      }
    }, [dataGrid.current, filesViewMode, isViewRendered, selectedFilesInIconsMode]);

    useEffect(() => {
      if (filesViewMode == fileViewModes.LARGE_ICONS_VIEW) {
        dataGrid.current = {};
      }
      setIsViewRendered(false);
    }, [filesViewMode]);

    useEffect(() => {
      onFileSelect(selectedFilesInIconsMode);
    }, [selectedFilesInIconsMode]);

    const [debounced, setDebounced] = useState(null);
    const [query, setQuery] = useState(null);
    const delay = 300;
    const [isOver, setIsOver] = useState(false);
    const dragCounter = useRef(0);

    useEffect(() => {
      const handler = setTimeout(() => setDebounced(query), delay);
      return () => clearTimeout(handler);
    }, [query]);

    useEffect(() => {
      if (debounced == null) {
        return;
      }
      if (debounced == "") {
        setShowPathColumn(false);
      } else {
        setShowPathColumn(true);
      }
      handleSearchFiles(debounced);
    }, [debounced]);

    const searchRef = useRef(null);

    useEffect(() => {
      setDebounced(null);
      setQuery(null);
      const searchEle: any = document.getElementById("fileSearchBox");
      if (searchEle) {
        searchEle.value = null;
      }
    }, [selectedFolder.id]);

    useEffect(() => {
      setColumnDefinitions(() => [
        ...fileColumns,
        ...(showPathColumn
          ? [
              {
                id: "path",
                field: "path",
                name: "Path",
                formatter: (data: any, params: any, a: any, b: any, value: any) =>
                  value.path ? `/${value.path.join("/")}` : "/",
                minWidth: 200,
              },
            ]
          : []),
      ]);
    }, [showPathColumn]);

    const handleClose = () => {
      setMenuState((s: any) => ({ ...s, source: "", open: false, position: null }));
    };

    useEffect(() => {
      const data = fileManagerData.filteredFiles || fileManagerData.files || [];
      let filteredItems = [];
      if (tempFolder.resourceType == "RESOURCE_TYPE_TRANSMITTAL") {
        filteredItems = _.cloneDeep(data).filter(
          (item: any) =>
            (item.transmittalVersionId == tempFolder.id && item.resourceType != "RESOURCE_TYPE_TRANSMITTAL") || query,
        );
      } else if (!tempFolder.resourceType) {
        filteredItems = _.cloneDeep(data).filter(
          (item: any) => (item.parentId == tempFolder.id && item.resourceType == "RESOURCE_TYPE_TRANSMITTAL") || query,
        );
      } else {
        filteredItems = _.cloneDeep(data).filter((item: any) => item.parentId == tempFolder.id || query);
      }
      onFileLoad(filteredItems);
      const folders = filteredItems.filter((resource: any) => resource.resourceType === "RESOURCE_TYPE_FOLDER");
      const resourceOtherThanFolders = filteredItems.filter(
        (resource: any) => resource.resourceType !== "RESOURCE_TYPE_FOLDER",
      );
      const sortedFolders: any = folders.sort((a: any, b: any) => a.name.localeCompare(b.name));
      const sortedRecources: any = resourceOtherThanFolders.sort((a: any, b: any) => a.name.localeCompare(b.name));
      setFilesData([...sortedFolders, ...sortedRecources]);
    }, [tempFolder.id, fileManagerData]);

    useEffect(() => {
      const hierarchy = [];
      if (tempFolder.id && tempFolder.parentIdArray?.length) {
        tempFolder.parentIdArray.map((id: any) => {
          const obj = fileManagerData.files?.find((item: any) => item.id == id);
          if (obj) {
            hierarchy.push(obj);
          }
        });
      }
      if (tempFolder.id != "0") {
        hierarchy.push(tempFolder);
      }
      let data = [
        {
          ...fileManagerData.cwd,
          hasChild: true,
          parentId: null,
          id: "0",
        },
      ];
      if (hierarchy?.length) {
        data = [...data, ...hierarchy];
      }
      setBreadCrumbData(data);
    }, [tempFolder.id, fileManagerData.cwd]);

    useEffect(() => {
      if (selectedResources?.length && dataGrid.current) {
        const indexes: any = [];
        const selectedResourceIds = selectedResources.map((r: any) => r.id);
        for (let i = 0; i < filesData.length; i++) {
          if (selectedResourceIds.includes(filesData[i].id) && !filesData[i].isRowDisabled) {
            indexes.push(i);
          }
        }
        setTimeout(() => {
          dataGrid.current?.slickGrid?.setSelectedRows(indexes);
        }, 0);
      }
    }, [filesData, dataGrid.current]);

    const recordClick = (clickedRow: any) => {
      if (
        clickedRow.resourceType === "RESOURCE_TYPE_FOLDER" ||
        clickedRow.resourceType === "RESOURCE_TYPE_TRANSMITTAL"
      ) {
        setSelectedFolder(clickedRow);
      }
      onFileClick(clickedRow);
    };

    const selectionHandlerSlickGrid = () => {
      const selectedData = dataGrid.current.slickGrid
        .getSelectedRows()
        .map((data: any) => dataGrid.current.slickGrid.getDataItem(data));
      const filteredData = selectedData.filter((file: any) => !!file);
      setSelectedResources(filteredData);
      onFileSelect(filteredData);
    };

    const searchFileHandler = (args: any) => {
      const value = args.target.value?.toLowerCase() || "";
      setQuery(value);
    };

    const searchFilesTemplate = () => {
      return (
        <div className="flex flex-1">
          <TextFieldComponent
            id="fileSearchBox"
            ref={searchRef}
            onChange={searchFileHandler}
            placeholder={`Search ${selectedFolder?.name ?? ""}`}
          />
        </div>
      );
    };

    const breacrumbTemplate = () => {
      return (
        <div className="flex gap-3 flex-3 overflow-auto ps-2 items-center">
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
            {breadCrumbData.map((item: any) => (
              <Link
                key={item?.id}
                underline="hover"
                sx={{
                  color: "#192048",
                }}
                fontSize={"13px"}
                className="cursor-pointer"
                onClick={() => setSelectedFolder(item)}
              >
                {item?.name}
              </Link>
            ))}
          </Breadcrumbs>
        </div>
      );
    };
    const toolbar = [
      {
        id: "breadcrumb",
        template: breacrumbTemplate,
      },
      {
        id: "searchFiles",
        rightAllign: true,
        template: searchFilesTemplate,
      },
    ];

    const handleContextMenuForResource = (e: any, file: any, selectedFiles: any) => {
      let options = ["RESOURCE_TYPE_FOLDER", "RESOURCE_TYPE_TRANSMITTAL"].includes(file.resourceType)
        ? contextMenuSettingsRef.current.folder
        : contextMenuSettingsRef.current.file;

      const disabledItems = menuOpen(selectedFiles);
      options = options.map((opt: any) => {
        opt.disabled = false;
        if (disabledItems.includes(opt.id)) {
          opt.disabled = true;
        }
        return opt;
      });
      setMenuState({
        options,
        source: file.resourceType === "RESOURCE_TYPE_FOLDER" ? "FOLDER" : "FILE",
        open: contextMenuSettingsRef.current.visible,
        position: {
          top: e.pageY,
          left: e.pageX,
        },
        data: file,
      });
    };

    const contextMenuForIconsView = (e: any, file: any) => {
      e.stopPropagation();
      e.preventDefault();
      const selectedFiles = [...selectedFilesInIconsMode];
      setSelectedFilesInIconsMode((prev: any) => [...prev, file]);
      handleContextMenuForResource(e, file, selectedFiles);
    };

    const handleGridCreated = (reactGrid: any) => {
      setIsViewRendered(true);
      reactGrid.slickGrid.onContextMenu.subscribe((e: any) => {
        e.preventDefault();
        const cell = reactGrid.slickGrid.getCellFromEvent(e);
        if (!cell) return;

        const row = cell.row;
        const dataView = reactGrid.slickGrid.getData();
        const item = dataView.getItem(row);

        if (!reactGrid?.slickGrid?.getSelectedRows().includes(row)) {
          reactGrid.slickGrid.setSelectedRows([row]);
        }
        const selectedFiles = reactGrid.slickGrid
          .getSelectedRows()
          .map((data: any) => reactGrid.slickGrid.getDataItem(data));
        handleContextMenuForResource(e, item, selectedFiles);
      });
      reactGrid.slickGrid.onDblClick.subscribe((e: any) => {
        e.preventDefault();
        const cell = reactGrid.slickGrid.getCellFromEvent(e);
        if (!cell) return;

        const row = cell.row;
        const dataView = reactGrid.slickGrid.getData();
        const item = dataView.getItem(row);
        recordClick(item);
      });
      reactGrid.slickGrid.onScroll.subscribe(() => {
        setMenuState((s: any) => (s.open ? { ...s, source: "", open: false, position: null } : s));
      });

      const emptyDiv = reactGrid.slickGrid.getContainerNode().querySelector(".slick-empty-data-warning");
      if (emptyDiv) {
        emptyDiv.classList.add("h-full");
        const root = createRoot(emptyDiv);
        root.render(
          <div className="flex flex-col items-center justify-center">
            <IconComponent name="folder" className="w-12 h-12 mb-2" />
            <div className="not-italic">{t("This folder is empty")}</div>
          </div>,
        );
      }
    };

    const onContextMenu = (args: any) => {
      if (!args.target.closest(".slick-row")) {
        if (menuState.position) {
          return;
        }
        args.preventDefault();
        let options = contextMenuSettings.layout;
        const disabledItems = menuOpen();
        options = options.map((opt: any) => {
          opt.disabled = false;
          if (disabledItems?.includes(opt.id)) {
            opt.disabled = true;
          }
          return opt;
        });
        setMenuState({
          options,
          source: "LAYOUT",
          open: contextMenuSettings.visible,
          position: {
            top: args.pageY,
            left: args.pageX,
          },
        });
      }
    };

    const isFileDrag = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).includes("Files");

    const onDragEnter = (e: any) => {
      if (!isFileDrag(e) || !allowDragAndDropUpload) return;
      e.preventDefault();
      dragCounter.current++;
      setIsOver(true);
    };
    const onDragLeave = (e: any) => {
      if (!isFileDrag(e) || !allowDragAndDropUpload) return;
      dragCounter.current = Math.max(0, dragCounter.current - 1);
      if (dragCounter.current === 0) {
        setIsOver(false);
      }
    };
    const onDragOver = (e: any) => {
      if (!isFileDrag(e) || !allowDragAndDropUpload) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    };
    const handleDrop = (e: any) => {
      if (!isFileDrag(e) || !allowDragAndDropUpload) return;
      e.preventDefault();
      dragCounter.current = 0;
      setIsOver(false);
      beforeSending({
        action: "upload",
        files: e.dataTransfer.files,
      });
    };

    const gridOptions = {
      enableContextMenu: false,
      enableGridMenu: false,
      forceFitColumns: true,
      enableColumnPicker: false,
      showEmptyDataMessage: true,
      emptyDataMessage: "",
    };

    const getToolBar = () => {
      return (
        <Box
          onContextMenu={onContextMenu}
          className="w-full justify-between gap-4 items-center flex min-h-12 h-12 pe-2 border-t border-button_light overflow-auto"
        >
          {toolbar.map((item) => item.template())}
        </Box>
      );
    };

    const getDetailsGrid = () => {
      return (
        <Box
          id="fileManagerDetailsView"
          className={`w-full h-full`}
          onDragEnter={onDragEnter}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={handleDrop}
        >
          <div
            className={`pointer-events-none absolute inset-0 z-20 flex items-center justify-center transition-opacity border-4 border-dashed border-blue-500 rounded-md
          ${isOver ? "opacity-100" : "opacity-0"}
        `}
          >
            <span className="px-4 py-2 rounded-md bg-white/70 text-blue-600 font-semibold shadow-sm">
              {t("Drop files here")}
            </span>
          </div>
          <Box className={`w-full h-full transition ${isOver ? "opacity-30" : ""}`} onContextMenu={onContextMenu}>
            <SlickGrid
              ref={dataGrid}
              disabledIds={disabledRows}
              toolbarHeight={15}
              enableSelectAllCheckbox
              onReactGridCreated={handleGridCreated}
              onSelectedRowIdsChanged={selectionHandlerSlickGrid}
              columnDefinitions={columnDefinitions}
              allowPagination={false}
              cssClass={"border-dashed border-2 border-blue-500"}
              dataset={filesData}
              gridOptions={gridOptions}
              allowSorting={false}
              allowFiltering={false}
              enableFiltering={false}
              toolbar={null}
              showToolbarBorder
            />
          </Box>
        </Box>
      );
    };
    const handleMenuClick = (item: any) => {
      let selectedData = [];
      if (filesViewMode == fileViewModes.DETAILED_VIEW) {
        selectedData = dataGrid.current.slickGrid
          .getSelectedRows()
          .map((data: any) => dataGrid.current.slickGrid.getDataItem(data));
      } else if (filesViewMode === fileViewModes.LARGE_ICONS_VIEW) {
        selectedData = selectedFilesInIconsMode;
      }

      if (item.id == "cut") {
        const ids = selectedData.map((file: any) => file.id);
        setDisabledRows(ids);
      } else if (item.id == "paste") {
        setDisabledRows([]);
      } else if (
        item.id == "open" &&
        (selectedData[0].resourceType === "RESOURCE_TYPE_FOLDER" ||
          selectedData[0].resourceType === "RESOURCE_TYPE_TRANSMITTAL")
      ) {
        setSelectedFolder(selectedData[0]);
      }
      if (item.onClick) {
        item.onClick(selectedData);
      }
      setMenuState((s: any) => ({ ...s, source: "", open: false, position: null }));
    };

    const renderMenuItems = (items = []) => {
      return items?.map((item: any) => {
        return (
          <MenuItem disabled={item.disabled} key={item.id} onClick={() => handleMenuClick(item)}>
            <div className="flex gap-4 text-sm items-center">
              {item.icon && <IconComponent name={item.icon} />}
              {item.name}
            </div>
          </MenuItem>
        );
      });
    };

    const checkIconBoxSelect = (args: any, file: any) => {
      let files = [...selectedFilesInIconsMode];
      if (args.target.checked) {
        files.push(file);
        setSelectedFilesInIconsMode([...files]);
      } else {
        files = files.filter((item) => item.id != file.id);
        setSelectedFilesInIconsMode([...files]);
      }
    };

    const getFilesIconsView = () => {
      setIsViewRendered(true);
      return (
        <Box
          id="fileManagerLargeIconsView"
          ref={dataGrid}
          onContextMenu={onContextMenu}
          className="p-5 border-t h-full border-button_light w-full overflow-auto"
        >
          <div className="flex flex-wrap gap-10 w-full overflow-auto">
            {filesData.map((file: any) => (
              <Box
                key={file.id}
                onDoubleClick={() => recordClick(file)}
                onContextMenu={(e) => contextMenuForIconsView(e, file)}
                className={`group flex relative flex-col p-2 h-max pt-3 rounded overflow-hidden cursor-pointer text-ellipsis whitespace-nowrap gap-1 w-20 ${selectedFilesInIconsMode.find((item: any) => item.id == file.id) ? "bg-card_selection_color" : "hover:bg-card_hover_color"} ${disabledRows.includes(file.id) || file.isRowDisabled ? "opacity-40 pointer-events-none" : ""}`}
              >
                <div
                  className={`absolute flex top-1 start-1 opacity-0 group-hover:opacity-100 transition-opacity ${selectedFilesInIconsMode.find((item: any) => item.id == file.id) ? "opacity-100" : ""}`}
                >
                  <CheckBoxComponent
                    onChange={(args) => checkIconBoxSelect(args, file)}
                    size="small"
                    checked={!!selectedFilesInIconsMode.find((item: any) => item.id == file.id)}
                    sx={{ padding: "0px" }}
                  />
                </div>

                <div className="items-center justify-center flex">{getIconbasedOnResourceType(file, "h-10")}</div>

                <div className="overflow-hidden text-center text-ellipsis whitespace-nowrap">{file.name}</div>
              </Box>
            ))}
          </div>
        </Box>
      );
    };

    const getFilesAccordingToSelectedView = () => {
      if (filesViewMode == fileViewModes.DETAILED_VIEW) {
        return getDetailsGrid();
      } else if (filesViewMode == fileViewModes.LARGE_ICONS_VIEW) {
        return getFilesIconsView();
      }
    };
    const getFilesAndTools = () => (
      <div className="flex items-center flex-col h-full w-full">
        {getToolBar()}
        {getFilesAccordingToSelectedView()}
      </div>
    );

    return (
      <div className="w-full h-full">
        {getFilesAndTools()}
        <Menu
          id="demo-positioned-menu"
          anchorReference="anchorPosition"
          anchorPosition={menuState.position}
          open={menuState.open}
          onClose={handleClose}
        >
          {renderMenuItems(menuState.options)}
        </Menu>
      </div>
    );
  },
);
Files.displayName = "Files";
export default Files;
